import { Stack } from '@mantine/core';
import { useCountPendingRequests } from '@repo/api-codegen';
import { UserRole } from '@repo/common/enums/UserRole';
import { Icon } from '@repo/foundations';
import { observer } from 'mobx-react-lite';
import { useMemo } from 'react';
import { useLocation } from 'react-router';
import {
	useAuthUser,
	usePolicyEnabled,
	useSuspenseFeatureAccess,
} from '../../../api';
import { useCheckIAMPermission } from '../../../utils/authorization/roles';
import { ensurePositive } from '../../../utils/utils';
import { isActive } from '../utils';
import { SideBarCategoryToggle } from './SideBarCategoryToggle';
import type { Link } from './SideBarLink';
import SideBarLink from './SideBarLink';

function SideBarWorkspaceLinks() {
	const location = useLocation();
	const policiesEnabled = usePolicyEnabled();
	const {
		analyticsAccess,
		monitoringAccess,
		darAccess,
		automationsAccess,
		secodaCoreAccess,
	} = useSuspenseFeatureAccess();
	const { workspace } = useAuthUser();

	const { hasPermission: hasPermissionToReadDataAccessRequests } =
		useCheckIAMPermission({
			v1AllowedRoles: [UserRole.ADMIN, UserRole.EDITOR, UserRole.VIEWER],
			v2Permission: 'DataAccessRequests.Read',
		});

	const showDataAccessRequests =
		!!workspace?.dar_enabled &&
		hasPermissionToReadDataAccessRequests &&
		!!darAccess;

	const { data: pendingRequestsCount } = useCountPendingRequests(
		{},
		{
			enabled: showDataAccessRequests,
		}
	);

	const { hasPermission: showIntegrations } = useCheckIAMPermission({
		v1AllowedRoles: [UserRole.ADMIN],
		v2Permission: 'Integrations.Manage',
	});

	const { hasPermission: hasMonitorPermission } = useCheckIAMPermission({
		v1AllowedRoles: [UserRole.ADMIN, UserRole.EDITOR],
		v2Permission: 'Monitors.Read',
	});

	const { hasPermission: hasAutomationPermission } = useCheckIAMPermission({
		v1AllowedRoles: [UserRole.ADMIN, UserRole.EDITOR],
		v2Permission: 'Automations.Read',
	});

	const { hasPermission: hasAnalyticsPermission } = useCheckIAMPermission({
		v1AllowedRoles: [UserRole.ADMIN, UserRole.EDITOR],
		v2Permission: 'Analytics.Read',
	});

	const { hasPermission: hasPolicyPermission } = useCheckIAMPermission({
		v1AllowedRoles: [UserRole.ADMIN, UserRole.EDITOR],
		v2Permission: 'Policies.Read',
	});

	const showAnalytics = hasAnalyticsPermission && analyticsAccess;
	const showPolicies = hasPolicyPermission && policiesEnabled;
	const showAutomations =
		hasAutomationPermission &&
		!!workspace?.automation_enabled &&
		!!automationsAccess;
	const showMonitors =
		hasMonitorPermission &&
		!!workspace?.monitoring_enabled &&
		!!monitoringAccess;

	const footerLinks: Array<Link> = useMemo(
		() =>
			[
				{
					id: 'nav::integrations',
					label: 'Integrations',
					icon: <Icon name="plugConnected" color="icon/primary/default" />,
					href: ['/integrations'],
					show: showIntegrations,
				},
				{
					id: 'nav::requests',
					label: 'Access requests',
					icon: <Icon name="lockOpen" color="icon/primary/default" />,
					href: ['/requests'],
					show: showDataAccessRequests && !!secodaCoreAccess,
					showCount: true,
					count: ensurePositive(pendingRequestsCount),
				},
				{
					id: 'nav::monitors',
					label: 'Monitors',
					icon: <Icon name="activity" color="icon/primary/default" />,
					href: ['/monitors', '/monitor'],
					show: showMonitors && !!secodaCoreAccess,
				},
				{
					id: 'nav::policies',
					label: 'Policies',
					icon: <Icon name="fileCheck" color="icon/primary/default" />,
					href: ['/policies', '/policy'],
					show: showPolicies && !!secodaCoreAccess,
				},
				{
					id: 'nav::automations',
					label: 'Automations',
					icon: <Icon name="bolt" color="icon/primary/default" />,
					href: ['/automations', '/automation'],
					show: showAutomations && !!secodaCoreAccess,
				},
				{
					id: 'nav::analytics',
					label: 'Analytics',
					icon: <Icon name="chartBar" color="icon/primary/default" />,
					href: ['/analytics'],
					show: !!showAnalytics && !!secodaCoreAccess,
				},
			].filter((l) => l.show),
		[
			showIntegrations,
			showDataAccessRequests,
			pendingRequestsCount,
			showMonitors,
			showPolicies,
			showAutomations,
			showAnalytics,
			secodaCoreAccess,
		]
	);

	if (!footerLinks?.length) {
		// We don't want to show "just the title 'Workspace'" to guests and viewers.
		return null;
	}

	return (
		<SideBarCategoryToggle
			label="Workspace"
			toggleKey="workspace-sidebar-collapsable-opened"
		>
			<Stack spacing={0} align="flex-start">
				{footerLinks.map((link) => (
					<SideBarLink
						key={link.id}
						link={link}
						selected={isActive(link.href, location)}
					/>
				))}
			</Stack>
		</SideBarCategoryToggle>
	);
}

const ObservedSideBarWorkspaceLinks = observer(SideBarWorkspaceLinks);
export default ObservedSideBarWorkspaceLinks;
